import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaStar, FaQuoteRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const Section = styled.section`
  padding: 6rem 5%;
  position: relative;
  overflow: hidden;
  min-height: 700px;
  display: flex;
  align-items: center;
  background-image: url('/images/testimonialbg.png');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  background-repeat: no-repeat;

   &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0.6) 100%
    );
    z-index: 1;
    pointer-events: none;
  }

  @media (max-width:768px){
    padding:3rem 4%;
    min-height:auto;
    background-attachment:scroll;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 3rem 5%;
    min-height: 500px;
  }
`;

const Container = styled.div`
  max-width:1400px;
  margin:0 auto;
  position:relative;
  z-index:2;
  width:100%;
  text-align: center;
`;

/* Title Strip */
const TitleStrip = styled.div`
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1rem 2.5rem;           /* ← Reduced from 1.5rem 3rem */
  display: inline-block;
  margin-bottom: 0.8rem;           /* ← Reduced from 1rem */
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.5);

  @media (max-width: 768px) {
    padding: 0.8rem 1.2rem;        /* ← Reduced from 1rem 1.5rem */
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.7rem 1.5rem;        /* ← Reduced from 1rem 2rem */
    margin-bottom: 0.5rem;         /* ← Reduced from 0.6rem */
  }
`;

/* Subtitle Strip */
const SubtitleStrip = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 0.5rem 1.5rem;          /* ← Reduced from 0.8rem 2rem */
  display: inline-block;
  margin-bottom: 3rem;             /* ← Reduced from 4rem */
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.4);

  @media (max-width: 768px) {
    padding: 0.4rem 1rem;          /* ← Reduced from 0.6rem 1.2rem */
    margin-bottom: 2rem;           /* ← Reduced from 2.5rem */
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.4rem 1.2rem;        /* ← Reduced from 0.5rem 1.5rem */
    margin-bottom: 1.5rem;         /* ← Reduced from 2rem */
  }
`;

const Title = styled.h2`
  font-size: 2.8rem;               /* ← Reduced from 3.2rem */
  color: #1a1a2e;
  font-family: 'Playfair Display', 'Georgia', serif;
  font-weight: 700;
  margin: 0;
  line-height: 1.3;

  span {
    color: #3730A3;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 100%;
      height: 3px;
      background: #3730A3;
      border-radius: 2px;
    }
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;             /* ← Reduced from 2rem */
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1.9rem;             /* ← Reduced from 2.2rem */
  }
`;

const Subtitle = styled.p`
  color: #4a4a6a;
  font-size: 1rem;                 /* ← Reduced from 1.15rem */
  font-family: 'Montserrat', 'Helvetica', sans-serif;
  font-weight: 400;
  margin: 0;
  line-height: 2.9;

  @media (max-width: 768px) {
    font-size: 0.85rem;            /* ← Reduced from 0.9rem */
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.85rem;            /* ← Reduced from 0.95rem */
  }
`;

const CarouselContainer = styled.div`
  position:relative;
  margin:0 auto;
  padding:0 70px;

  @media (max-width:768px){
    padding:0 15px;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0 50px;
  }
`;

const CardsWrapper = styled.div`
  overflow:hidden;
  padding:1.5rem 0;

  @media (max-width:600px){
    padding:1rem 0;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.8rem 0;
  }
`;

const CardsTrack = styled.div`
  display:flex;
  transition:transform 0.6s cubic-bezier(0.645,0.045,0.355,1);
  gap:2rem;

  @media (max-width:600px){
    gap:0;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 1.5rem;
  }
`;

const TestimonialCard = styled.div`
  flex:0 0 calc(33.333% - 1.33rem);
  background:rgba(255,255,255,0.95);
  backdrop-filter:blur(10px);
  border-radius:30px;
  padding:2.5rem;
  position:relative;
  box-shadow:0 20px 40px rgba(0,0,0,0.15);
  font-family:'Lora','Georgia',serif;
  border:1px solid rgba(55, 48, 163, 0.1);
  transition:all 0.4s ease;

  &:hover{
    transform:translateY(-15px) scale(1.02);
    box-shadow:0 30px 50px rgba(0,0,0,0.2);
    background:rgba(255,255,255,1);
    border-color:#ffd966;
  }

  @media (max-width:1000px){
    flex:0 0 calc(50% - 1rem);
    padding:2rem;
  }

  @media (max-width:600px){
    flex:0 0 100%;
    padding:1.3rem;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1.8rem;
    border-radius: 22px;
  }
`;

const QuoteIcon = styled.div`
  position:absolute;
  top:1.5rem;
  right:1.5rem;
  font-size:3rem;
  color:#ffd966;
  opacity:0.3;
  animation:${float} 3s ease-in-out infinite;

  @media (max-width:600px){
    font-size:2rem;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2rem;
    top: 1rem;
    right: 1rem;
  }
`;

const Rating = styled.div`
  color:#ffc107;
  margin-bottom:1.5rem;
  display:flex;
  gap:0.3rem;
  font-size:1.1rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-bottom: 1rem;
    font-size: 0.95rem;
  }
`;

const TestimonialText = styled.p`
  color:#2c3e50;
  font-size:1.05rem;
  line-height:1.8;
  margin-bottom:2rem;
  font-style:italic;
  font-family:'Lora','Georgia',serif;
  position:relative;
  padding-left:1.5rem;

  &::before{
    content:'"';
    position:absolute;
    left:0;
    top:-15px;
    font-size:3rem;
    color:#ffd966;
    opacity:0.3;
    font-family:'Playfair Display',serif;
  }

  @media (max-width:600px){
    font-size:0.9rem;
    line-height:1.6;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 1.2rem;
    padding-left: 1rem;

    &::before {
      font-size: 2rem;
      top: -10px;
    }
  }
`;

const StudentInfo = styled.div`
  display:flex;
  align-items:center;
  gap:1.2rem;
  border-top:1px solid rgba(55, 48, 163, 0.1);
  padding-top:1.5rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 0.8rem;
    padding-top: 1rem;
  }
`;

const StudentAvatar = styled.div`
  width:65px;
  height:65px;
  border-radius:50%;
  background:linear-gradient(135deg,#3730A3,#0b0b77);
  color:white;
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:bold;
  font-size:1.4rem;
  font-family:'Montserrat',sans-serif;
  box-shadow:0 5px 15px rgba(55, 48, 163, 0.3);
  border:3px solid #3730A3;

  @media (max-width:600px){
    width:50px;
    height:50px;
    font-size:1rem;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    width: 50px;
    height: 50px;
    font-size: 1.1rem;
    border: 2px solid #3730A3;
  }
`;

const StudentDetails = styled.div`
  h4{
    color:#3730A3;
    margin-bottom:0.3rem;
    font-size:1.2rem;
    font-family:'Montserrat','Helvetica',sans-serif;
    font-weight:600;
  }

  p{
    color:#5a6b7a;
    font-size:0.9rem;
    font-family:'Open Sans','Helvetica',sans-serif;
    font-weight:500;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    h4 {
      font-size: 1rem;
      margin-bottom: 0.2rem;
    }
    p {
      font-size: 0.8rem;
    }
  }
`;

const NavigationButton = styled.button`
  position:absolute;
  top:50%;
  transform:translateY(-50%);
  width:55px;
  height:55px;
  border-radius:50%;
  background:rgba(255,255,255,0.9);
  border:2px solid #ffd966;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:1.5rem;
  color:#3730A3;
  transition:all 0.4s ease;
  z-index:3;
  box-shadow:0 5px 15px rgba(0,0,0,0.2);
  backdrop-filter:blur(5px);

  &:hover{
    background:#ffd966;
    transform:translateY(-50%) scale(1.15);
  }

  &.left{
    left:0;
  }

  &.right{
    right:0;
  }

  @media (max-width:768px){
    width:40px;
    height:40px;
    font-size:1rem;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    width: 42px;
    height: 42px;
    font-size: 1.2rem;
  }
`;

const DotsContainer = styled.div`
  display:flex;
  justify-content:center;
  gap:15px;
  margin-top:3rem;

  @media (max-width:768px){
    margin-top:1.5rem;
    gap:8px;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 1.5rem;
    gap: 10px;
  }
`;

const Dot = styled.button`
  width:14px;
  height:14px;
  border-radius:50%;
  background:${props=>props.active?'#ffd966':'rgba(255,255,255,0.6)'};
  border:2px solid ${props=>props.active?'#ffd966':'transparent'};
  cursor:pointer;

  @media (max-width: 1366px) and (min-width: 769px) {
    width: 10px;
    height: 10px;
  }
`;

const TestimonialsSection = () => {

const testimonials=[
{
name:"Nithin Kumar",
role:"CCS Certified",
avatar:"NK",
text:"The instructors at Learn Thru Us are amazing! Their ICD-10 training was comprehensive and helped me pass my CCS exam on the first attempt.",
rating:5
},
{
name:"Sarah Khan",
role:"CPC, CDI Specialist",
avatar:"SK",
text:"I switched my career to medical coding through Learn Thru Us. Their job placement assistance and interview preparation were invaluable.",
rating:5
},
{
name:"Ravindra Kumar",
role:"Inpatient Coder",
avatar:"RK",
text:"The DRG coding training was exactly what I needed. The real-world examples prepared me for inpatient coding.",
rating:5
},
{
name:"Hemanth Hari",
role:"HCC Coding Specialist",
avatar:"HH",
text:"The HCC/Risk Adjustment training gave me the edge I needed in my career.",
rating:5
},
{
name:"Priya Sharma",
role:"CDI Specialist",
avatar:"PS",
text:"Learn Thru Us Academy transformed my career completely.",
rating:5
},
{
name:"Ashritha Patel",
role:"Cancer Registry Specialist",
avatar:"AP",
text:"The ODS training program was comprehensive and well structured.",
rating:5
}
];

const [currentIndex,setCurrentIndex]=useState(0);
const [cardsToShow,setCardsToShow]=useState(3);

useEffect(()=>{
const handleResize=()=>{
if(window.innerWidth<=600){
setCardsToShow(1);
}else if(window.innerWidth<=1000){
setCardsToShow(2);
}else{
setCardsToShow(3);
}
};

handleResize();
window.addEventListener('resize',handleResize);
return()=>window.removeEventListener('resize',handleResize);
},[]);

const handlePrev=()=>setCurrentIndex(i=>Math.max(0,i-1));
const handleNext=()=>setCurrentIndex(i=>Math.min(testimonials.length-cardsToShow,i+1));

const maxIndex=testimonials.length-cardsToShow;

return(
<Section>
<Container>

<div>
  {/* <TitleStrip> */}
    <Title>
      What Our <span>Students Say</span>
    </Title>
  {/* </TitleStrip> */}
</div>

<div>
  {/* <SubtitleStrip> */}
    <Subtitle>
      Real stories from successful certified professionals who transformed their careers with us
    </Subtitle>
  {/* </SubtitleStrip> */}
</div>

<CarouselContainer>

{currentIndex>0 &&
<NavigationButton className="left" onClick={handlePrev}>
<FaChevronLeft/>
</NavigationButton>
}

<CardsWrapper>

<CardsTrack
style={{
transform:`translateX(-${currentIndex*(100/cardsToShow)}%)`
}}
>

{testimonials.map((testimonial,index)=>(
<TestimonialCard key={index}>

<QuoteIcon><FaQuoteRight/></QuoteIcon>

<Rating>
{[...Array(testimonial.rating)].map((_,i)=>(
<FaStar key={i}/>
))}
</Rating>

<TestimonialText>
{testimonial.text}
</TestimonialText>

<StudentInfo>

<StudentAvatar>
{testimonial.avatar}
</StudentAvatar>

<StudentDetails>
<h4>{testimonial.name}</h4>
<p>{testimonial.role}</p>
</StudentDetails>

</StudentInfo>

</TestimonialCard>
))}

</CardsTrack>

</CardsWrapper>

{currentIndex<maxIndex &&
<NavigationButton className="right" onClick={handleNext}>
<FaChevronRight/>
</NavigationButton>
}

</CarouselContainer>

<DotsContainer>

{[...Array(maxIndex+1)].map((_,index)=>(
<Dot
key={index}
active={currentIndex===index}
onClick={()=>setCurrentIndex(index)}
/>
))}

</DotsContainer>

</Container>
</Section>
);
};

export default TestimonialsSection;